import { useEffect, useState } from 'react';
import { cacheFilterOptions } from '#features/useDemandOptions';
import type { RefundOptimizerVo } from './constants';

/**
 * 退款优化师下拉选项（FR-14）— 接真实 filter_options，替代 mock 硬编码。
 *
 * 链路：需求单 id(demand_id) → filter_options(optimizer_user, demand_id)
 * → 后端 {@link OptimizerUserOption.resolveProductIds} 按 demand.productId 反查 optimizer_config
 * 配置的启用优化师（productIds 为空则返回空列表）。
 *
 * 入参语义：需求单 id（一级单优先，二级单可回落）——后端 CommonOptionsForm.demandId 是单个值，
 * 多单场景前端并发逐 id 请求后聚合去重（同一一级单下多二级单=同产品，选项去重）。
 *
 * 批量场景（聚合取）：所有选中需求单并发查询 → 统一优化师选项集，所有行共用；
 * 跨产品时显示并集，用户自行筛选。
 */
export type RefundOptimizerOption = RefundOptimizerVo & {
  value: number;
  label: string;
};

export function useRefundOptimizerOptions(
  demandIds: number[],
  enabled = true
): RefundOptimizerOption[] {
  const [options, setOptions] = useState<RefundOptimizerOption[]>([]);
  // 数组引用稳定性：调用方常传内联数组，join 后作 effect 依赖（避免每次渲染重复请求）
  const key = demandIds.join(',');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!enabled || !key) return;
      const ids = key
        .split(',')
        .map(Number)
        .filter((id) => Number.isFinite(id) && id > 0);
      if (!ids.length) return;
      try {
        // 需求单 id → filter_options(optimizer_user, demand_id)：后端按 demand.productId 反查 optimizer_config，
        // 替代旧链路（l2→demandDetail.product_id→product_ids），少一次请求且语义对齐 OptimizerUserOption
        const results = await Promise.all(
          ids.map((id) => cacheFilterOptions({ keys: [OPTIMIZER_KEY], demand_id: id }))
        );
        const rawList = results.flatMap((data: unknown) => {
          const list =
            (data as Array<{ key?: string; options?: OptimizerRaw[] }> | undefined) ?? [];
          return list.find((item) => item.key === OPTIMIZER_KEY)?.options ?? [];
        });
        // 多个需求单可能同产品（同一一级单下多二级单）→ 优化师按 value 去重
        const seen = new Set<number>();
        const dedup = rawList.filter((o: OptimizerRaw) => {
          const v = Number(o.value);
          if (!Number.isFinite(v) || seen.has(v)) return false;
          seen.add(v);
          return true;
        });
        if (!cancelled) setOptions(toOptimizerOptions(dedup));
      } catch {
        if (!cancelled) setOptions([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [key, enabled]);

  return options;
}

/** filter_options 优化师 key（后端 OptimizerUserOption.optionKey()，按需求单产品反查 optimizer_config） */
const OPTIMIZER_KEY = 'optimizer_user';

/** filter_options 返回的优化师原始项（后端 UserOption：{ name, value }） */
type OptimizerRaw = {
  name?: string;
  value?: number;
};

/** 后端 { name, value } → RefundOptimizerOption（user_id/name 供 userSelectFormat 映射，value/label 供 select 显示） */
function toOptimizerOptions(list: OptimizerRaw[]): RefundOptimizerOption[] {
  return list.map((o) => ({
    user_id: Number(o.value),
    name: o.name ?? '',
    user_name: o.name ?? '',
    value: Number(o.value),
    label: o.name ?? '',
  }));
}
